import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import MainStage from './components/MainStage';
import Modal from './components/Modal';
import supabase from './supabase/supabase';

function Toast({ message, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 5000);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-[#1a1a1a] border border-[#2e2e2e] text-white text-xs font-medium tracking-wide px-5 py-3.5 rounded-xl shadow-2xl pointer-events-none select-none">
      <span className="w-1.5 h-1.5 rounded-full bg-white shrink-0" />
      {message}
    </div>
  );
}

function App() {
  const [activeRoom, setActiveRoom]         = useState(null);
  const [rooms, setRooms]                   = useState([]);
  const [modal, setModal]                   = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [toast, setToast]                   = useState(null);
  const navigate                            = useNavigate();

  const dismissToast = useCallback(() => setToast(null), []);

  useEffect(() => {
    document.title = 'studiotimer / admin';

    const checkSession = async () => {
      // If a recovery token was detected at page load, never render the dashboard.
      if (sessionStorage.getItem('st_recovery') === '1') {
        navigate('/login', { replace: true });
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login', { replace: true });
        return;
      }
      setIsCheckingAuth(false);
    };

    checkSession();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (sessionStorage.getItem('st_recovery') === '1') {
        navigate('/login', { replace: true });
        return;
      }
      if (event === 'USER_UPDATED' && session) {
        setToast('Your email address has been successfully updated.');
        setIsCheckingAuth(false);
        return;
      }
      if (event === 'SIGNED_OUT' || !session) {
        navigate('/login', { replace: true });
      } else if (session) {
        setIsCheckingAuth(false);
      }
    });

    return () => authListener.subscription?.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (isCheckingAuth) return;
    const fetchRooms = async () => {
      const { data, error } = await supabase.from('rooms').select('*').order('name');
      if (error) { console.error('Error fetching rooms:', error); return; }
      if (data) {
        setRooms(data);
        if (data.length > 0) {
          const savedId    = localStorage.getItem('selected_room_id');
          const targetRoom = data.find((r) => r.id === savedId) || data[0];
          setActiveRoom(targetRoom);
        }
      }
    };
    fetchRooms();
  }, [isCheckingAuth]);

  const openAddRoom    = ()     => setModal({ type: 'add' });
  const openDeleteRoom = (room) => setModal({ type: 'delete', room });
  const openEditRoom   = (room) => setModal({ type: 'edit',   room });
  const closeModal     = ()     => setModal(null);

  const getUniqueRoomName = (baseName, excludeId = null) => {
    let finalName = baseName;
    let counter   = 1;
    while (rooms.some((r) => r.id !== excludeId && r.name.toLowerCase() === finalName.toLowerCase())) {
      finalName = `${baseName} ${counter}`;
      counter++;
    }
    return finalName;
  };

  const handleAddRoom = async (name) => {
    const trimmed = name?.trim();
    if (!trimmed) { closeModal(); return; }
    const uniqueName = getUniqueRoomName(trimmed);
    const { data, error } = await supabase.from('rooms').insert({ name: uniqueName }).select().single();
    if (error) { console.error('Error adding room:', error); alert('Error adding room: ' + error.message); }
    else if (data) {
      setRooms((prev) => [...prev, data]);
      localStorage.setItem('selected_room_id', data.id);
      setActiveRoom(data);
    }
    closeModal();
  };

  const handleEditRoom = async (roomObj, newName) => {
    const trimmed = newName?.trim();
    if (!trimmed) { closeModal(); return; }
    const uniqueName = getUniqueRoomName(trimmed, roomObj.id);
    if (uniqueName === roomObj.name) { closeModal(); return; }
    const { data, error } = await supabase.from('rooms').update({ name: uniqueName }).eq('id', roomObj.id).select().single();
    if (error) { console.error('Error updating room:', error); alert('Error updating room: ' + error.message); }
    else if (data) {
      setRooms((prev) => prev.map((r) => (r.id === roomObj.id ? data : r)));
      if (activeRoom?.id === roomObj.id) setActiveRoom(data);
    }
    closeModal();
  };

  const handleDeleteRoom = async (roomObj) => {
    const { error } = await supabase.from('rooms').delete().eq('id', roomObj.id);
    if (error) { console.error('Error deleting room:', error); alert('Error deleting room: ' + error.message); }
    else {
      const remaining = rooms.filter((r) => r.id !== roomObj.id);
      setRooms(remaining);
      if (activeRoom?.id === roomObj.id) {
        const nextRoom = remaining[0] || null;
        if (nextRoom) localStorage.setItem('selected_room_id', nextRoom.id);
        else          localStorage.removeItem('selected_room_id');
        setActiveRoom(nextRoom);
      }
    }
    closeModal();
  };

  if (isCheckingAuth) {
    return <div className="flex h-screen bg-obsidian text-white items-center justify-center" />;
  }

  return (
    <div className="flex h-screen bg-obsidian text-white overflow-hidden selection:bg-white selection:text-black">
      <Sidebar
        activeRoom={activeRoom}
        setActiveRoom={setActiveRoom}
        rooms={rooms}
        onAddRoom={openAddRoom}
        onDeleteRoom={openDeleteRoom}
        onEditRoom={openEditRoom}
      />
      {activeRoom ? (
        <MainStage key={activeRoom.id} activeRoom={activeRoom} />
      ) : (
        <main className="flex-1 h-full flex flex-col items-center justify-center bg-obsidian pt-[128px] md:pt-0 text-muted">
          <p className="text-sm tracking-widest uppercase font-semibold">No Rooms Available</p>
        </main>
      )}
      {modal && (
        <Modal
          type={modal.type}
          room={modal.room}
          onConfirmAdd={handleAddRoom}
          onConfirmEdit={handleEditRoom}
          onConfirmDelete={handleDeleteRoom}
          onClose={closeModal}
        />
      )}
      {toast && <Toast message={toast} onDone={dismissToast} />}
    </div>
  );
}

export default App;